import { GAME_CONSTANTS } from '@tankworld/shared';
import { angleDifference, distanceVec3 } from './math.js';
export class BotController {
    vehicle;
    difficulty;
    targetWaypoints = [
        { x: -50, y: 0, z: -50 },
        { x: 50, y: 0, z: -50 },
        { x: 50, y: 0, z: 50 },
        { x: -50, y: 0, z: 50 },
    ];
    currentWaypointIndex = 0;
    stuckTimer = 0;
    unstuckActionTimer = 0;
    lastPosition = { x: 0, y: 0, z: 0 };
    fireCooldownTimer = 0;
    constructor(vehicle, difficulty = 'normal') {
        this.vehicle = vehicle;
        this.difficulty = difficulty;
        this.currentWaypointIndex = Math.floor(Math.random() * this.targetWaypoints.length);
    }
    update(dt, otherVehicles, obstacles, terrain) {
        if (!this.vehicle.isAlive) {
            return {
                input: { sequence: 0, throttle: 0, steer: 0, brake: true, turretYaw: this.vehicle.hullYaw, gunPitch: 0 },
                wantsToFire: false,
            };
        }
        this.fireCooldownTimer += dt;
        // Detect if tank is stuck (low movement over time while throttle applied)
        const distMoved = distanceVec3(this.vehicle.position, this.lastPosition);
        this.lastPosition = { ...this.vehicle.position };
        if (distMoved < 0.2) {
            this.stuckTimer += dt;
            if (this.stuckTimer > 2.0 && this.unstuckActionTimer <= 0) {
                this.unstuckActionTimer = 1.8; // Reverse & turn maneuver
                this.stuckTimer = 0;
            }
        }
        else {
            this.stuckTimer = Math.max(0, this.stuckTimer - dt);
        }
        if (this.unstuckActionTimer > 0) {
            this.unstuckActionTimer -= dt;
            return {
                input: {
                    sequence: 0,
                    throttle: -0.8,
                    steer: 0.9,
                    brake: false,
                    turretYaw: this.vehicle.turretYaw,
                    gunPitch: 0,
                },
                wantsToFire: false,
            };
        }
        // 1. Target selection: find nearest living non-self tank
        let nearestTarget = null;
        let minDistance = Infinity;
        for (const v of otherVehicles) {
            if (v.id === this.vehicle.id || !v.isAlive)
                continue;
            // In free-for-all, target anyone living
            const d = distanceVec3(this.vehicle.position, v.position);
            if (d < minDistance) {
                minDistance = d;
                nearestTarget = v;
            }
        }
        // 2. Navigation planning
        let targetMovePoint;
        const combatRange = this.difficulty === 'hard' ? 45 : 55;
        if (nearestTarget && minDistance < 140) {
            if (minDistance < 25) {
                // Too close: back up
                targetMovePoint = {
                    x: this.vehicle.position.x - (nearestTarget.position.x - this.vehicle.position.x),
                    y: 0,
                    z: this.vehicle.position.z - (nearestTarget.position.z - this.vehicle.position.z),
                };
            }
            else {
                // Move towards target flank
                const flankOffset = this.difficulty === 'hard' ? 20 : 0;
                targetMovePoint = {
                    x: nearestTarget.position.x + flankOffset,
                    y: nearestTarget.position.y,
                    z: nearestTarget.position.z,
                };
            }
        }
        else {
            // Follow waypoints
            const wp = this.targetWaypoints[this.currentWaypointIndex];
            targetMovePoint = wp;
            if (distanceVec3(this.vehicle.position, wp) < 15) {
                this.currentWaypointIndex = (this.currentWaypointIndex + 1) % this.targetWaypoints.length;
            }
        }
        // 3. Vehicle steering calculation
        const toTarget = {
            x: targetMovePoint.x - this.vehicle.position.x,
            z: targetMovePoint.z - this.vehicle.position.z,
        };
        const desiredHullYaw = Math.atan2(toTarget.x, toTarget.z);
        let hullYawDiff = angleDifference(desiredHullYaw, this.vehicle.hullYaw);
        let throttle = 0.75;
        let steer = Math.sign(hullYawDiff) * Math.min(1.0, Math.abs(hullYawDiff) * 1.5);
        // Obstacle avoidance feelers
        const feelerDist = 14.0;
        const feelerX = this.vehicle.position.x + Math.sin(this.vehicle.hullYaw) * feelerDist;
        const feelerZ = this.vehicle.position.z + Math.cos(this.vehicle.hullYaw) * feelerDist;
        for (const obs of obstacles) {
            const dx = feelerX - obs.position.x;
            const dz = feelerZ - obs.position.z;
            if (dx * dx + dz * dz < (obs.radius + 3.0) * (obs.radius + 3.0)) {
                // Obstacle detected ahead! Steer away
                steer = -Math.sign(steer || 1.0);
                throttle *= 0.5;
                break;
            }
        }
        // 4. Turret aiming and lead prediction
        let turretYaw = this.vehicle.turretYaw;
        let gunPitch = 0;
        let wantsToFire = false;
        if (nearestTarget && minDistance < 150) {
            // Calculate lead targeting based on projectile speed
            const projSpeed = GAME_CONSTANTS.PROJECTILE_SPEED;
            const flightTime = minDistance / projSpeed;
            const predictedTargetPos = {
                x: nearestTarget.position.x + nearestTarget.velocity.x * flightTime,
                y: nearestTarget.position.y,
                z: nearestTarget.position.z + nearestTarget.velocity.z * flightTime,
            };
            const aimDx = predictedTargetPos.x - this.vehicle.position.x;
            const aimDz = predictedTargetPos.z - this.vehicle.position.z;
            const aimDy = (predictedTargetPos.y + 1.2) - (this.vehicle.position.y + GAME_CONSTANTS.TURRET_HEIGHT_OFFSET);
            const horizDist = Math.sqrt(aimDx * aimDx + aimDz * aimDz);
            turretYaw = Math.atan2(aimDx, aimDz);
            // Ballistic arc compensation: theta ≈ (y / horizDist) + 0.5 * g * t^2 / horizDist
            const gravityComp = 0.5 * GAME_CONSTANTS.PROJECTILE_GRAVITY * flightTime * flightTime;
            gunPitch = Math.atan2(aimDy + gravityComp, horizDist);
            // Check alignment for firing
            const aimDiff = Math.abs(angleDifference(turretYaw, this.vehicle.turretYaw));
            const aimTolerance = this.difficulty === 'hard' ? 0.08 : (this.difficulty === 'normal' ? 0.15 : 0.25);
            if (aimDiff < aimTolerance && this.vehicle.canFire() && this.fireCooldownTimer > 0.4) {
                wantsToFire = true;
                this.fireCooldownTimer = 0;
            }
        }
        return {
            input: {
                sequence: 0,
                throttle,
                steer,
                brake: false,
                turretYaw,
                gunPitch,
            },
            wantsToFire,
        };
    }
}
//# sourceMappingURL=bot.js.map